import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  MessageCircle,
  Search,
  Send,
  Plus,
  Users,
  User,
  Hash,
  ChevronRight,
  Bell,
  LogOut,
  Settings,
  MoreVertical,
  CheckCircle,
  Circle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// NSB Logo Component
import { NSBLogo } from "../components/NSBLogo";

export default function MessagingPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [users, setUsers] = useState([]);
  const [showNewDM, setShowNewDM] = useState(false);
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [newChannelName, setNewChannelName] = useState("");
  const [newChannelDescription, setNewChannelDescription] = useState("");
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedChannel) {
      fetchMessages(selectedChannel.channel_id);
      const interval = setInterval(() => fetchMessages(selectedChannel.channel_id), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedChannel]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const userRes = await fetch(`${API_URL}/api/auth/me`, {
        credentials: "include",
        headers,
      });

      if (!userRes.ok) {
        navigate("/login");
        return;
      }

      const userData = await userRes.json();
      setUser(userData);

      // Fetch channels
      const channelsRes = await fetch(`${API_URL}/api/messaging/channels`, {
        credentials: "include",
        headers,
      });
      if (channelsRes.ok) {
        const channelsData = await channelsRes.json();
        setChannels(channelsData.channels || []);
      }

      // Fetch users for new DM
      const usersRes = await fetch(`${API_URL}/api/messaging/users`, {
        credentials: "include",
        headers,
      });
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.users || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (channelId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/messages/${channelId}?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChannel) return;

    setSendingMessage(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          channel_id: selectedChannel.channel_id,
          content: newMessage.trim(),
          message_type: "text",
        }),
      });

      if (res.ok) {
        setNewMessage("");
        fetchMessages(selectedChannel.channel_id);
      } else {
        toast.error("Failed to send message");
      }
    } catch (error) {
      toast.error("Error sending message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCreateDM = async (recipientId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/messaging/dm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ recipient_id: recipientId }),
      });

      if (res.ok) {
        const data = await res.json();
        setShowNewDM(false);
        fetchData(); // Refresh channels
        setSelectedChannel(data.channel);
        toast.success(data.is_new ? "Conversation started!" : "Opened conversation");
      } else {
        toast.error("Failed to create conversation");
      }
    } catch (error) {
      toast.error("Error creating conversation");
    }
  };

  const handleCreateTeamChannel = async () => {
    if (!newChannelName.trim()) {
      toast.error("Channel name is required");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/messaging/team-channel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newChannelName.trim(),
          description: newChannelDescription.trim(),
          participants: selectedParticipants,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setShowNewChannel(false);
        setNewChannelName("");
        setNewChannelDescription("");
        setSelectedParticipants([]);
        fetchData();
        setSelectedChannel(data.channel);
        toast.success("Channel created!");
      } else {
        toast.error("Failed to create channel");
      }
    } catch (error) {
      toast.error("Error creating channel");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "scout": return "bg-blue-100 text-blue-800";
      case "coach": return "bg-green-100 text-green-800";
      case "athlete": return "bg-purple-100 text-purple-800";
      case "admin": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col" data-testid="messaging-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <NSBLogo className="h-10" light={false} />
            </Link>
            <div className="h-6 w-px bg-gray-300" />
            <div>
              <h1 className="text-lg font-bold text-gray-900">Team Messaging</h1>
              <p className="text-xs text-gray-500">Direct Messages & Team Channels</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon">
              <Bell className="h-5 w-5 text-gray-600" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="h-4 w-4 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-gray-700">{user?.name || user?.email}</span>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex max-w-7xl mx-auto w-full">
        {/* Sidebar - Channels */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-900">Conversations</h2>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => setShowNewDM(true)} title="New DM">
                  <User className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setShowNewChannel(true)} title="New Channel">
                  <Hash className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search conversations..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Channel List */}
          <div className="flex-1 overflow-y-auto">
            {channels.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <MessageCircle className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No conversations yet</p>
                <p className="text-xs mt-1">Start a new DM or create a channel</p>
              </div>
            ) : (
              channels.map((channel) => (
                <button
                  key={channel.channel_id}
                  onClick={() => setSelectedChannel(channel)}
                  className={`w-full p-3 flex items-center gap-3 hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                    selectedChannel?.channel_id === channel.channel_id ? "bg-blue-50 border-l-4 border-l-blue-500" : ""
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    channel.channel_type === "dm" ? "bg-blue-100" : "bg-purple-100"
                  }`}>
                    {channel.channel_type === "dm" ? (
                      <User className="h-5 w-5 text-blue-600" />
                    ) : (
                      <Hash className="h-5 w-5 text-blue-900" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="font-medium text-gray-900 truncate">
                      {channel.display_name || channel.name}
                    </div>
                    {channel.last_message_preview && (
                      <p className="text-xs text-gray-500 truncate">{channel.last_message_preview}</p>
                    )}
                  </div>
                  {channel.unread_count > 0 && (
                    <span className="bg-blue-500 text-white text-xs rounded-full px-2 py-0.5">
                      {channel.unread_count}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col bg-white">
          {selectedChannel ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    selectedChannel.channel_type === "dm" ? "bg-blue-100" : "bg-purple-100"
                  }`}>
                    {selectedChannel.channel_type === "dm" ? (
                      <User className="h-5 w-5 text-blue-600" />
                    ) : (
                      <Hash className="h-5 w-5 text-blue-900" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{selectedChannel.display_name || selectedChannel.name}</h3>
                    <p className="text-xs text-gray-500">
                      {selectedChannel.channel_type === "dm" ? "Direct Message" : 
                       `${selectedChannel.participants?.length || 0} members`}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="h-5 w-5 text-gray-500" />
                </Button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <MessageCircle className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No messages yet</p>
                    <p className="text-sm">Send a message to start the conversation</p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isOwn = msg.sender_id === user?.user_id;
                    return (
                      <div
                        key={msg.message_id}
                        className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                      >
                        <div className={`max-w-[70%] ${isOwn ? "order-2" : "order-1"}`}>
                          {!isOwn && (
                            <p className="text-xs text-gray-500 mb-1 ml-1">{msg.sender_name}</p>
                          )}
                          <div className={`rounded-lg px-4 py-2 ${
                            isOwn 
                              ? "bg-blue-500 text-white rounded-br-none" 
                              : "bg-gray-100 text-gray-900 rounded-bl-none"
                          }`}>
                            <p className="text-sm">{msg.content}</p>
                          </div>
                          <p className={`text-xs text-gray-400 mt-1 ${isOwn ? "text-right mr-1" : "ml-1"}`}>
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200">
                <div className="flex gap-2">
                  <Input
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    className="flex-1"
                    disabled={sendingMessage}
                    data-testid="message-input"
                  />
                  <Button type="submit" disabled={sendingMessage || !newMessage.trim()} data-testid="send-message-btn">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <MessageCircle className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Select a conversation</h3>
                <p className="text-sm">Choose a conversation from the sidebar or start a new one</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New DM Modal */}
      {showNewDM && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">New Direct Message</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowNewDM(false)}>×</Button>
            </div>
            <div className="p-4">
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mb-4"
              />
              <div className="max-h-80 overflow-y-auto space-y-2">
                {filteredUsers.map((u) => (
                  <button
                    key={u.user_id}
                    onClick={() => handleCreateDM(u.user_id)}
                    className="w-full p-3 flex items-center gap-3 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-900 flex items-center justify-center text-white font-bold">
                      {u.name?.charAt(0) || u.email?.charAt(0)}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-medium text-gray-900">{u.name || u.email}</div>
                      <div className="text-xs text-gray-500">{u.email}</div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${getRoleColor(u.role)}`}>
                      {u.role}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Channel Modal */}
      {showNewChannel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Create Team Channel</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowNewChannel(false)}>×</Button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Channel Name</label>
                <Input
                  placeholder="e.g., Dallas Combine Team"
                  value={newChannelName}
                  onChange={(e) => setNewChannelName(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Description (optional)</label>
                <Input
                  placeholder="What's this channel about?"
                  value={newChannelDescription}
                  onChange={(e) => setNewChannelDescription(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Add Members</label>
                <div className="max-h-40 overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-2">
                  {users.slice(0, 20).map((u) => (
                    <button
                      key={u.user_id}
                      type="button"
                      onClick={() => {
                        if (selectedParticipants.includes(u.user_id)) {
                          setSelectedParticipants(selectedParticipants.filter(id => id !== u.user_id));
                        } else {
                          setSelectedParticipants([...selectedParticipants, u.user_id]);
                        }
                      }}
                      className={`w-full p-2 flex items-center gap-2 rounded transition-colors ${
                        selectedParticipants.includes(u.user_id) ? "bg-blue-50" : "hover:bg-gray-50"
                      }`}
                    >
                      {selectedParticipants.includes(u.user_id) ? (
                        <CheckCircle className="h-4 w-4 text-blue-500" />
                      ) : (
                        <Circle className="h-4 w-4 text-gray-300" />
                      )}
                      <span className="text-sm">{u.name || u.email}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${getRoleColor(u.role)}`}>{u.role}</span>
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={handleCreateTeamChannel} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Create Channel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
